import { useEffect, useState } from "react";
import { ApiService } from "../../../../services/api";
import { BookOpen, Target, Video, FileText, ClipboardCheck, Calendar, Clock } from "lucide-react";
import checkPermission from "../../../../utils/permission";
import { useNavigate } from "react-router-dom";

export default function LessonHomeworkView({ lessonId, sectionId }: { lessonId: string; sectionId?: string }) {
  const navigate = useNavigate();
  const canList = checkPermission("homework_list");

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<any[]>([]);

  useEffect(() => {
    fetchHomeworks();
  }, [lessonId, sectionId]);

  const fetchHomeworks = async () => {
    if (!canList) return;
    setLoading(true);
    try {
      const res = await ApiService.getLessonHomeworks(lessonId, {
        status: "PENDING",
        lessonSectionId: sectionId,
        sortBy: "createdAt",
        sortOrder: "DESC",
        limit: 20,
      });
      const allItems = res.data || [];
      setItems(allItems);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  if (!canList) {
    return <div className="text-center text-gray-600">Bu alanı görüntüleme yetkiniz yok.</div>;
  }

  const goToHomework = (sectionId: string, contentId: string) => {
    navigate(`/lessons/play/${lessonId}?sectionId=${sectionId}&contentId=${contentId}`);
  };

  const getHomeworkStatus = (status: string, deadline?: string, metadata?: any) => {
    if (status === 'COMPLETED' && metadata?.completedLate) return 'Geç Tamamlandı';

    if (deadline) {
      const now = new Date();
      const deadlineDate = new Date(deadline);
      const isOverdue = now > deadlineDate;

      if (isOverdue && (status === "PENDING" || status === "STARTED")) {
        return "Süresi Geçti";
      }
    }

    switch (status) {
      case "PENDING":
        return "Bekliyor";
      case "STARTED":
        return "Devam Ediyor";
      case "COMPLETED":
        return "Tamamlandı";
      case "FAILED":
        return "Başarısız";
      default:
        return "Bekliyor";
    }
  };

  const getStatusBadgeClass = (status: string, deadline?: string, metadata?: any) => {
    if (status === 'COMPLETED' && metadata?.completedLate) return 'bg-amber-100 text-amber-800 border-amber-200';

    if (deadline) {
      const now = new Date();
      const deadlineDate = new Date(deadline);
      const isOverdue = now > deadlineDate;

      if (isOverdue && (status === "PENDING" || status === "STARTED")) {
        return "bg-orange-100 text-orange-800 border-orange-200";
      }
    }

    switch (status) {
      case "PENDING":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "STARTED":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "COMPLETED":
        return "bg-green-100 text-green-800 border-green-200";
      case "FAILED":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getContentIcon = (type?: string) => {
    if (type === "VIDEO") {
      return <Video className="w-4 h-4 text-purple-600" />;
    } else if (type === "TEST" || type === "EXAM") {
      return <ClipboardCheck className="w-4 h-4 text-emerald-600" />;
    } else if (type === "FILE") {
      return <FileText className="w-4 h-4 text-blue-600" />;
    } else {
      return <FileText className="w-4 h-4 text-gray-600" />;
    }
  };

  const formatDeadlineDate = (deadline: string) => {
    const date = new Date(deadline);
    return date.toLocaleDateString("tr-TR", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getRemainingTime = (deadline: string) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate.getTime() - now.getTime();

    if (diffTime <= 0) {
      return "Süresi Geçti";
    }

    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return "Bugün Bitiyor";
    } else if (diffDays === 1) {
      return "1 Gün Kaldı";
    } else if (diffDays <= 7) {
      return `${diffDays} Gün Kaldı`;
    } else if (diffDays <= 30) {
      const weeks = Math.floor(diffDays / 7);
      return weeks === 1 ? "1 Hafta Kaldı" : `${weeks} Hafta Kaldı`;
    } else {
      const months = Math.floor(diffDays / 30);
      return months === 1 ? "1 Ay Kaldı" : `${months} Ay Kaldı`;
    }
  };

  return (
    <div className="space-y-4">
      {loading ? (
        <div className="text-center text-gray-500">Yükleniyor...</div>
      ) : items.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{sectionId ? "Bu Bölümde Bekleyen Ödev Yok" : "Bekleyen Ödev Yok"}</h3>
          <p className="text-gray-500">
            {sectionId ? "Bu bölüm için bekleyen bir ödev bulunmuyor." : "Tüm ödevlerinizi tamamladınız veya henüz ödev atanmamış."}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((homework) => (
            <div
              key={homework.id}
              className="border border-gray-200 rounded-lg p-3 sm:p-4 hover:bg-gray-50 hover:border-purple-300 transition-all duration-200 cursor-pointer group"
              onClick={() => {
                if (homework.lessonSectionContentId) {
                  goToHomework(homework.lessonSectionId, homework.lessonSectionContentId);
                }
              }}>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                <div className="flex-1 space-y-2 min-w-0">
                  <div className="flex items-center gap-2">
                    <Target className="h-4 w-4 text-indigo-600 flex-shrink-0" />
                    <span className="text-xs sm:text-sm text-gray-700 font-medium truncate">
                      {homework.lessonSection?.title || "(Bölüm Bulunamadı)"}
                    </span>
                  </div>

                  <div className="flex items-start gap-2">
                    <div className="flex-shrink-0 mt-0.5">{getContentIcon(homework.lessonSectionContent?.type)}</div>
                    <span className="text-sm sm:text-base text-gray-900 font-semibold line-clamp-2">
                      {homework.lessonSectionContent?.title || "(İçerik Bulunamadı)"}
                    </span>
                  </div>

                  {homework.deadline && (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0" />
                        <span className="text-gray-600 truncate">{formatDeadlineDate(homework.deadline)}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0" />
                        <span
                          className={`font-medium ${getRemainingTime(homework.deadline) === "Süresi Geçti"
                            ? "text-red-600"
                            : getRemainingTime(homework.deadline) === "Bugün Bitiyor"
                              ? "text-red-500"
                              : getRemainingTime(homework.deadline).includes("Gün")
                                ? "text-orange-600"
                                : "text-green-600"
                            }`}>
                          {getRemainingTime(homework.deadline)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex-shrink-0 self-start sm:self-auto">
                  <span
                    className={`inline-block px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs font-medium border ${getStatusBadgeClass(
                      homework.status,
                      homework.deadline,
                      homework.metadata,
                    )}`}>
                    {getHomeworkStatus(homework.status, homework.deadline, homework.metadata)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
